import React, { useEffect, useRef, useState } from 'react';
import {
  PlayIcon,
  BalanceIcon,
  CursorIcon,
  QualityIcon,
  ScopeIcon,
  ShieldIcon,
  SyntheticIcon,
  AnalyticsIcon
} from './Icons';
import './LandingPage.css';
import ContactModal from './ContactModal';

function LandingPage({ onStartDemo }) {
  const gridRef = useRef(null);
  const [showContactModal, setShowContactModal] = useState(false);

  useEffect(() => {
    const grid = gridRef.current;
    if (grid) {
      grid.style.opacity = '1';
    }
  }, []);

  return (
    <div className="lp-container">
      {/* Ambient Background */}
      <div className="lp-grid-bg" ref={gridRef}>
        <div className="lp-grid-lines"></div>
        <div className="lp-glow-orb lp-glow-1"></div>
        <div className="lp-glow-orb lp-glow-2"></div>
      </div>

      {/* Navigation */}
      <nav className="lp-nav">
        <div className="lp-nav-logo">
          <img src="/favicon.png" alt="TouchBase" className="lp-logo-icon" />
          <span>TouchBase</span>
        </div>
        <div className="lp-nav-links">
          <button
            className="lp-nav-link"
            onClick={() => setShowContactModal(true)}
          >
            Contact
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="lp-hero">
        <div className="lp-hero-content">
          <h1 className="lp-hero-title">
            <span className="lp-title-sm">The Complete</span>
            <span className="lp-title-lg">Forensic Training</span>
            <span className="lp-title-accent">& Operations Platform</span>
          </h1>

          <p className="lp-hero-desc">
            Innovative software to measure competency and proficiency of latent
            print analysis and examination using advanced analytics and
            artificial intelligence.
          </p>

          <div className="lp-hero-actions">
            <button className="lp-btn-primary" onClick={onStartDemo}>
              <PlayIcon size={16} />
              <span>Try SCOPE Demo</span>
            </button>
          </div>

          {/* <div className="lp-hero-stats">
            <div className="lp-hero-stat">
              <span className="lp-stat-num">99.9%</span>
              <span className="lp-stat-txt">Uptime</span>
            </div>
            <div className="lp-stat-sep"></div>
            <div className="lp-hero-stat">
              <span className="lp-stat-num">CJIS</span>
              <span className="lp-stat-txt">Compliant</span>
            </div>
            <div className="lp-stat-sep"></div>
            <div className="lp-hero-stat">
              <span className="lp-stat-num">SOC 2</span>
              <span className="lp-stat-txt">Certified</span>
            </div>
          </div> */}
        </div>
      </header>

      {/* Capabilities Section */}
      <section className="lp-modules">
        <div className="lp-section-label">
          <div className="lp-label-line"></div>
          <span>SCOPE CAPABILITIES</span>
          <div className="lp-label-line"></div>
        </div>

        <div className="lp-capabilities-grid">
          {/* DECISION BALANCE */}
          <article className="lp-capability lp-capability-balance">
            <div className="lp-capability-header">
              <div className="lp-capability-icon">
                <BalanceIcon size={24} />
              </div>
              <h3>DECISION BALANCE</h3>
            </div>
            <p className="lp-capability-question">Are Examiners Being Thorough?</p>
            <p className="lp-capability-desc">
              Measure how balanced examiners are during comparisons - are they rushing? Being too cautious? Missing areas? Showing bias?
            </p>
            <ul className="lp-capability-list">
              <li>
                <span className="lp-list-label"><span className="lp-check">&#10003;</span>Search balance</span>
                <span className="lp-list-desc">Exploring enough vs. dwelling too long</span>
              </li>
              <li>
                <span className="lp-list-label"><span className="lp-check">&#10003;</span>Flexibility</span>
                <span className="lp-list-desc">Adapting to unclear evidence vs. forcing conclusions</span>
              </li>
              <li>
                <span className="lp-list-label"><span className="lp-check">&#10003;</span>Confidence</span>
                <span className="lp-list-desc">Working efficiently vs. gathering enough support</span>
              </li>
              <li>
                <span className="lp-list-label"><span className="lp-check">&#10003;</span>Balance scores</span>
                <span className="lp-list-desc">Simple metrics for quality review</span>
              </li>
              <li>
                <span className="lp-list-label"><span className="lp-check">&#10003;</span>Warning flags</span>
                <span className="lp-list-desc">Identify unbalanced examination patterns</span>
              </li>
            </ul>
            <div className="lp-capability-glow balance"></div>
          </article>

          {/* BEHAVIOR TRACKING */}
          <article className="lp-capability lp-capability-behavior">
            <div className="lp-capability-header">
              <div className="lp-capability-icon">
                <CursorIcon size={24} />
              </div>
              <h3>BEHAVIOR TRACKING</h3>
            </div>
            <p className="lp-capability-question">Watch How Examiners Think</p>
            <p className="lp-capability-desc">
              Record mouse movements during comparisons to reveal attention patterns, work habits, and examination strategy.
            </p>
            <ul className="lp-capability-list">
              <li>
                <span className="lp-list-label"><span className="lp-check">&#10003;</span>Path playback</span>
                <span className="lp-list-desc">See exactly how they worked through the print</span>
              </li>
              <li>
                <span className="lp-list-label"><span className="lp-check">&#10003;</span>Attention heatmaps</span>
                <span className="lp-list-desc">Where did they spend time?</span>
              </li>
              <li>
                <span className="lp-list-label"><span className="lp-check">&#10003;</span>Search patterns</span>
                <span className="lp-list-desc">Systematic or scattered approach?</span>
              </li>
              <li>
                <span className="lp-list-label"><span className="lp-check">&#10003;</span>Timing analysis</span>
                <span className="lp-list-desc">Rushed or thorough?</span>
              </li>
              <li>
                <span className="lp-list-label"><span className="lp-check">&#10003;</span>Coverage maps</span>
                <span className="lp-list-desc">Did they examine everything?</span>
              </li>
            </ul>
            <div className="lp-capability-glow behavior"></div>
          </article>

          {/* AUTOMATED QUALITY */}
          <article className="lp-capability lp-capability-quality">
            <div className="lp-capability-header">
              <div className="lp-capability-icon">
                <QualityIcon size={24} />
              </div>
              <h3>AUTOMATED QUALITY</h3>
            </div>
            <p className="lp-capability-question">Let The Computer Score Print Clarity</p>
            <p className="lp-capability-desc">
              Automatic analysis of fingerprint quality based on ridge clarity - standardize assessments across all examiners.
            </p>
            <ul className="lp-capability-list">
              <li>
                <span className="lp-list-label"><span className="lp-check">&#10003;</span>Automated clarity scoring</span>
                <span className="lp-list-desc">Computer rates ridge quality</span>
              </li>
              <li>
                <span className="lp-list-label"><span className="lp-check">&#10003;</span>Color-coded ratings</span>
                <span className="lp-list-desc">Visual quality levels (high/medium/low)</span>
              </li>
              <li>
                <span className="lp-list-label"><span className="lp-check">&#10003;</span>Consistent standards</span>
                <span className="lp-list-desc">Same scoring for everyone</span>
              </li>
              <li>
                <span className="lp-list-label"><span className="lp-check">&#10003;</span>Quality overlays</span>
                <span className="lp-list-desc">See clarity across the entire print</span>
              </li>
            </ul>
            <div className="lp-capability-glow quality"></div>
          </article>
        </div>
      </section>

      {/* SCOPE Interface Section */}
      <section className="lp-scope-section">
        <div className="lp-scope-intro">
          <div className="lp-scope-badge">
            <ScopeIcon size={14} />
            <span>SCOPE INTERFACE</span>
          </div>
          <h2>Record Everything, Miss Nothing</h2>
          <p>
            Unlike traditional comparison tools, SCOPE captures every mouse movement, every pause, every decision - creating a complete record of how the examiner worked.
          </p>
          <ul className="lp-scope-highlights">
            <li><span className="lp-check">&#10003;</span>Complete session recording - Every action captured automatically</li>
            <li><span className="lp-check">&#10003;</span>Instant replay - Watch examinations like game film</li>
            <li><span className="lp-check">&#10003;</span>Multi-layer visualization - Path, heatmap, clarity, and quality views</li>
            <li><span className="lp-check">&#10003;</span>Training mode - Learn from experienced examiners</li>
            <li><span className="lp-check">&#10003;</span>Quality review - Supervisors see exactly what happened</li>
            <li><span className="lp-check">&#10003;</span>No guesswork - Know how conclusions were reached</li>
          </ul>
        </div>

        <div className="lp-scope-features">
          <div className="lp-scope-feature">
            <div className="lp-feature-num">01</div>
            <h4>Layer-Based Composition</h4>
            <p>Work with multiple image layers — base prints, clarity maps, quality assessments, and heatmap overlays.</p>
          </div>
          <div className="lp-scope-feature">
            <div className="lp-feature-num">02</div>
            <h4>Complete Session Recording</h4>
            <p>Every movement and annotation captured in real-time, creating complete examination records.</p>
          </div>
          <div className="lp-scope-feature">
            <div className="lp-feature-num">03</div>
            <h4>Dynamic Equilibrium Analysis</h4>
            <p>Real-time calculation of exploration/exploitation ratios to measure examination thoroughness.</p>
          </div>
          <div className="lp-scope-feature">
            <div className="lp-feature-num">04</div>
            <h4>Attention Heatmaps</h4>
            <p>Visualize examiner focus patterns. Identify missed areas and coverage gaps.</p>
          </div>
          <div className="lp-scope-feature">
            <div className="lp-feature-num">05</div>
            <h4>Session Playback</h4>
            <p>Review examinations with full playback. Supervisors watch examinations step-by-step.</p>
          </div>
          <div className="lp-scope-feature">
            <div className="lp-feature-num">06</div>
            <h4>Color Coded Annotations</h4>
            <p>Industry-standard minutiae marking with directional indicators.</p>
          </div>
        </div>
      </section>

      {/* Agency Management Section */}
      <section className="lp-feature-section">
        <div className="lp-feature-content">
          <div className="lp-feature-text">
            <div className="lp-feature-badge">
              <ShieldIcon size={14} />
              <span>AGENCY MANAGEMENT</span>
            </div>
            <h2>Centralized Command Center</h2>
            <p>
              Eliminate paperwork and disparate spreadsheets. TouchBase provides
              unified control for laboratory directors and training coordinators.
            </p>
            <ul className="lp-feature-list">
              <li><span className="lp-check">&#10003;</span>Role-Based Access — Granular permissions for Admins, Analysts, and Trainees</li>
              <li><span className="lp-check">&#10003;</span>Job Assignment — Distribute casework and training sets with a single click</li>
              <li><span className="lp-check">&#10003;</span>Audit Logs — Immutable records of all system actions</li>
            </ul>
          </div>
          <div className="lp-feature-visual">
            <div className="lp-mock-dashboard">
              <div className="lp-mock-header">
                <span>AGENCY DASHBOARD</span>
                <div className="lp-mock-dots">
                  <span></span><span></span><span></span>
                </div>
              </div>
              <div className="lp-mock-stats">
                <div className="lp-mock-stat">
                  <span className="lp-mock-val">142</span>
                  <span className="lp-mock-lbl">Active Cases</span>
                </div>
                <div className="lp-mock-stat">
                  <span className="lp-mock-val">28</span>
                  <span className="lp-mock-lbl">Pending</span>
                </div>
                <div className="lp-mock-stat">
                  <span className="lp-mock-val">12</span>
                  <span className="lp-mock-lbl">Examiners</span>
                </div>
              </div>
              <div className="lp-mock-rows">
                <div className="lp-mock-row header"></div>
                <div className="lp-mock-row"></div>
                <div className="lp-mock-row"></div>
                <div className="lp-mock-row"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Synthetic Training Section */}
      <section className="lp-feature-section lp-synthetic">
        <div className="lp-feature-content reverse">
          <div className="lp-feature-visual">
            <div className="lp-pair-demo">
              <div className="lp-print lp-latent">
                <span className="lp-print-lbl">LATENT</span>
                <div className="lp-print-img"></div>
              </div>
              <div className="lp-pair-arrow">
                <span className="lp-arrow-symbol">&#8594;</span>
              </div>
              <div className="lp-print lp-known">
                <span className="lp-print-lbl">KNOWN</span>
                <div className="lp-print-img"></div>
              </div>
            </div>
            <div className="lp-pair-caption">SYNTHETIC PAIR • GROUND TRUTH VERIFIED</div>
          </div>
          <div className="lp-feature-text">
            <div className="lp-feature-badge amber">
              <SyntheticIcon size={14} />
              <span>SYNTHETIC TRAINING</span>
            </div>
            <h2>Revolutionary Ground Truth</h2>
            <p>
              Move beyond historical casework limitations. Generate mathematically-precise
              synthetic fingerprint pairs with 100% known outcomes for objective error measurement.
            </p>
            <ul className="lp-feature-list">
              <li><span className="lp-check">&#10003;</span>Infinite Variety — Thousands of unique pairs across all pattern types</li>
              <li><span className="lp-check">&#10003;</span>Absolute Ground Truth — Every minutia known for objective error rates</li>
              <li><span className="lp-check">&#10003;</span>Bias Mitigation — Targeted training sets to identify cognitive biases</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Analytics Section */}
      <section className="lp-feature-section">
        <div className="lp-feature-content">
          <div className="lp-feature-text">
            <div className="lp-feature-badge cyan">
              <AnalyticsIcon size={14} />
              <span>PERFORMANCE ANALYTICS</span>
            </div>
            <h2>Data-Driven Evaluation</h2>
            <p>
              Move beyond subjective feedback. TouchBase captures thousands of data
              points during every examination for objective performance metrics.
            </p>
            <ul className="lp-feature-list">
              <li><span className="lp-check">&#10003;</span>Accuracy Heatmaps — Visualize examiner focus and identify gaps</li>
              <li><span className="lp-check">&#10003;</span>Consistency Scoring — Compare against ground truth and peer consensus</li>
              <li><span className="lp-check">&#10003;</span>Automated Reporting — Generate proficiency reports for accreditation</li>
            </ul>
          </div>
          <div className="lp-feature-visual">
            <div className="lp-mock-analytics">
              <div className="lp-mock-header">
                <span>PERFORMANCE ANALYTICS</span>
                <div className="lp-mock-dots">
                  <span></span><span></span><span></span>
                </div>
              </div>
              <div className="lp-mock-chart">
                <div className="lp-chart-bar" style={{height: '55%'}}></div>
                <div className="lp-chart-bar" style={{height: '78%'}}></div>
                <div className="lp-chart-bar" style={{height: '42%'}}></div>
                <div className="lp-chart-bar" style={{height: '91%'}}></div>
                <div className="lp-chart-bar" style={{height: '67%'}}></div>
              </div>
              <div className="lp-mock-labels">
                <span>Accuracy</span>
                <span>Speed</span>
                <span>Coverage</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="lp-cta">
        <div className="lp-cta-content">
          <h2>Experience SCOPE</h2>
          <p>Interactive demonstration of the forensic comparison editor</p>
          <button className="lp-cta-btn" onClick={onStartDemo}>
            <PlayIcon size={18} />
            <span>LAUNCH DEMO</span>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="lp-footer">
        <div className="lp-footer-content">
          <div className="lp-footer-brand">
            <div className="lp-footer-logo">
              <img src="/favicon.png" alt="TouchBase" className="lp-logo-icon" />
              <span>TouchBase</span>
            </div>
            <p>The standard for forensic training and operations.</p>
          </div>
          <div className="lp-footer-links">
            <div className="lp-footer-col">
              <h4>Platform</h4>
              <button onClick={onStartDemo}>SCOPE Demo</button>
            </div>
            <div className="lp-footer-col">
              <h4>Company</h4>
              <a href="https://touchbaseforensics.com/about" target="_blank" rel="noopener noreferrer">About Us</a>
              <button onClick={() => setShowContactModal(true)}>Contact</button>
            </div>
          </div>
        </div>
        <div className="lp-footer-bottom">
          <div className="lp-footer-line"></div>
          <p>&copy; {new Date().getFullYear()} TouchBase Forensics</p>
        </div>
      </footer>

      {/* Contact Modal */}
      <ContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
      />
    </div>
  );
}

export default LandingPage;
